import { createTheme } from '@mui/material/styles';

const getDesignTokens = (mode) => ({
    palette: {
        mode,
        ...(mode === 'light'
            ? {
                // Light Mode
                primary: {
                    main: '#00B0CC', // Darker Cyan for light mode
                    light: '#69FFFF',
                    dark: '#008399',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#00B248', // Darker Green for light mode
                    light: '#66FFA6',
                    dark: '#008033',
                    contrastText: '#fff',
                },
                background: {
                    default: '#f8fafc', // Light Gray/White
                    paper: 'rgba(255, 255, 255, 0.8)',
                },
                text: {
                    primary: '#0f172a', // Slate 900
                    secondary: '#475569', // Slate 600
                },
                action: {
                    hover: 'rgba(0, 176, 204, 0.08)',
                },
            }
            : {
                // Dark Mode (Existing)
                primary: {
                    main: '#00F0FF', // Electric Blue/Cyan
                    light: '#69FFFF',
                    dark: '#00B0CC',
                    contrastText: '#000',
                },
                secondary: {
                    main: '#00E676', // Emerald Green
                    light: '#66FFA6',
                    dark: '#00B248',
                    contrastText: '#000',
                },
                background: {
                    default: '#050505', // True Black
                    paper: 'rgba(20, 20, 20, 0.4)', // "Frosted Ice"
                },
                text: {
                    primary: '#FFFFFF',
                    secondary: 'rgba(255, 255, 255, 0.7)',
                },
                action: {
                    hover: 'rgba(0, 240, 255, 0.1)',
                },
            }),
    },
    typography: {
        fontFamily: ['Outfit', 'Inter', 'sans-serif'].join(','),
        h1: {
            fontSize: '4rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            background: mode === 'dark' ? 'linear-gradient(to right, #fff, #b0b0b0)' : 'linear-gradient(to right, #0f172a, #334155)',
            WebkitBackgroundClip: 'text',
            // WebkitTextFillColor: 'transparent', // Logic often needs this with gradient text
        },
        h2: {
            fontSize: '3rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: '2rem',
            fontWeight: 700,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            letterSpacing: '0.02em',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    scrollbarColor: mode === 'dark' ? '#333 #050505' : '#cbd5e1 #f1f5f9',
                    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                        backgroundColor: 'transparent',
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                        borderRadius: 8,
                        backgroundColor: mode === 'dark' ? '#333' : '#cbd5e1',
                        minHeight: 24,
                        border: `2px solid ${mode === 'dark' ? '#050505' : '#f1f5f9'}`,
                    },
                    '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
                        backgroundColor: mode === 'dark' ? '#555' : '#94a3b8',
                    },
                    '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
                        backgroundColor: mode === 'dark' ? '#555' : '#94a3b8',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    padding: '12px 28px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                },
                containedPrimary: {
                    background: mode === 'dark' ? '#00F0FF' : '#00B0CC',
                    color: mode === 'dark' ? '#000000' : '#ffffff',
                    boxShadow: mode === 'dark' ? '0 0 15px rgba(0, 240, 255, 0.3)' : '0 4px 14px rgba(0, 176, 204, 0.3)',
                    '&:hover': {
                        background: mode === 'dark' ? '#00D6E6' : '#0097b0',
                        boxShadow: mode === 'dark' ? '0 0 30px rgba(0, 240, 255, 0.6)' : '0 6px 20px rgba(0, 176, 204, 0.5)',
                        transform: 'translateY(-2px)',
                    },
                },
                outlinedPrimary: {
                    borderColor: mode === 'dark' ? 'rgba(0, 240, 255, 0.5)' : 'rgba(0, 176, 204, 0.5)',
                    color: mode === 'dark' ? '#00F0FF' : '#00B0CC',
                    '&:hover': {
                        borderColor: mode === 'dark' ? '#00F0FF' : '#00B0CC',
                        background: mode === 'dark' ? 'rgba(0, 240, 255, 0.1)' : 'rgba(0, 176, 204, 0.08)',
                        boxShadow: mode === 'dark' ? '0 0 20px rgba(0, 240, 255, 0.2)' : '0 2px 10px rgba(0, 176, 204, 0.15)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backdropFilter: 'blur(40px)',
                    borderTop: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.4)'}`,
                    borderLeft: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.2)'}`,
                    borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.2)'}`,
                    borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.2)'}`,
                    boxShadow: mode === 'light' ? '0 4px 20px rgba(0,0,0,0.05)' : undefined,
                },
            },
        },
    },
});

const getTheme = (mode) => createTheme(getDesignTokens(mode));

export default getTheme;
